(() => {
  if (!window.customElements || customElements.get('maxler-protein-bundle')) return;

  const escapeHTML = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[char]));
  const idKey = (ids) => ids.map(String).sort().join('+');
  const calculatePrice = (variants, itemCount) => {
    const tier = itemCount === 3 ? 15 : itemCount === 2 ? 10 : 0;
    const current = variants.reduce((sum, variant) => sum + Number(variant.price || 0), 0);
    const regular = variants.reduce((sum, variant) => sum + Math.max(Number(variant.price || 0), Number(variant.compareAt || 0)), 0);
    const expected = Math.round(variants.reduce((sum, variant) => sum + Math.min(Number(variant.price || 0), Math.max(Number(variant.price || 0), Number(variant.compareAt || 0)) * (1 - tier / 100)), 0));
    return {current, regular, expected, savings: Math.max(0, regular - expected)};
  };

  class MaxlerProteinBundle extends HTMLElement {
    constructor() {
      super();
      this.abortController = null;
      this.boundClick = this.onClick.bind(this);
      this.boundChange = this.onChange.bind(this);
      this.boundDialogClose = this.onDialogClose.bind(this);
      this.products = [];
      this.selected = new Set();
      this.dialogProduct = null;
      this.dialogVariant = null;
      this.dialogFocus = null;
      this.submitting = false;
      this.cartOutcomeUnknown = false;
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      try {
        this.selected = new Set();
        const dataNode = this.querySelector('script[data-bundle-data]');
        this.data = JSON.parse(dataNode?.textContent || '{}');
        this.products = Array.isArray(this.data.products) ? this.data.products : [];
        if (!this.products.length) throw new Error('Bundle product data is empty');
        this.offers = Array.isArray(this.data.offers) ? this.data.offers : [];
        this.labels = this.data.labels || {};
        this.currency = this.data.currency || 'USD';
        this.locale = this.data.locale || document.documentElement.lang || undefined;
        this.products.forEach((product) => {
          product.variants = Array.isArray(product.variants) ? product.variants : [];
          product.options = Array.isArray(product.options) ? product.options : [];
          const initial = this.variantFor(product, product.selectedId);
          const firstAvailable = product.variants.find((variant) => this.usable(variant));
          product.selectedVariantId = String(this.usable(initial) ? initial.id : (firstAvailable?.id ?? ''));
          if (product.selected !== false && firstAvailable) this.selected.add(String(product.id));
        });
        this.cards = this.querySelector('[data-bundle-cards]');
        this.total = this.querySelector('[data-bundle-total]');
        this.regular = this.querySelector('[data-bundle-regular]');
        this.savings = this.querySelector('[data-bundle-savings]');
        this.message = this.querySelector('[data-bundle-message]');
        this.addButton = this.querySelector('[data-bundle-add]');
        this.dialog = this.querySelector('[data-bundle-dialog]');
        this.dialogTitle = this.querySelector('[data-bundle-dialog-title]');
        this.dialogContent = this.querySelector('[data-bundle-dialog-content]');
        this.dialogSave = this.querySelector('[data-bundle-save]');
        this.dialogClose = this.querySelector('[data-bundle-close]');
        if (!this.cards || !this.dialog || !this.dialogContent || !this.addButton) return;
        this.cards.classList.add('pb-card-list');
        this.addEventListener('click', this.boundClick);
        this.addEventListener('change', this.boundChange);
        this.dialog.addEventListener('close', this.boundDialogClose);
        this.renderCards();
        this.renderTotals();
        this.classList.add('is-enhanced');
        if (this.cartOutcomeUnknown) this.setMessage(this.label('error'), true);
      } catch (error) {
        this.setMessage(this.label('error'));
        if (this.addButton) this.addButton.disabled = true;
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.boundClick);
      this.removeEventListener('change', this.boundChange);
      this.dialog?.removeEventListener('close', this.boundDialogClose);
      this.abortController?.abort();
      this.abortController = null;
      this.initialized = false;
      this.classList.remove('is-enhanced');
      this.selected.clear();
      this.products = [];
      this.dialogProduct = null;
      this.dialogVariant = null;
    }

    label(key) { return this.labels[key] || key; }
    variantFor(product, id) { return product.variants.find((variant) => String(variant.id) === String(id)) || null; }
    usable(variant) { return !!variant && variant.allowed !== false && variant.available === true; }
    chosenVariant(product) {
      const variant = this.variantFor(product, product.selectedVariantId || product.selectedId);
      return this.usable(variant) ? variant : null;
    }
    money(cents) {
      const value = Number(cents || 0) / 100;
      if (window.theme?.Currency?.formatMoney && this.data.moneyFormat) {
        const formatted = window.theme.Currency.formatMoney(Number(cents || 0), this.data.moneyFormat);
        const node = document.createElement('span'); node.innerHTML = formatted; return node.textContent;
      }
      try { return new Intl.NumberFormat(this.locale, {style: 'currency', currency: this.currency}).format(value); }
      catch { return `${value.toFixed(2)} ${this.currency}`; }
    }
    formatOptionName(name, kind) {
      const normalized = String(name || '');
      return kind === 'flavour' || /flavou?r/i.test(normalized) ? this.label('flavour').toLocaleUpperCase(this.locale) : normalized;
    }
    variantOption(variant, option, index) {
      if (Array.isArray(variant.options)) return String(variant.options[index] ?? '');
      return String(variant[option.name] ?? '');
    }
    canSelectVariant(variant) { return this.usable(variant); }

    renderCards() {
      this.cards.innerHTML = this.products.map((product, index) => {
        const variant = this.chosenVariant(product);
        const checked = this.selected.has(String(product.id));
        const thumb = variant?.image || product.image;
        const selectedName = variant?.title && variant.title !== 'Default Title' ? variant.title.replaceAll('&amp;', '&') : this.label('choose');
        const description = variant ? `${escapeHTML(selectedName)} · ${this.money(variant.price)}` : escapeHTML(this.label('choose'));
        return `<article class="pb-card${checked ? ' is-selected' : ''}" data-product-id="${escapeHTML(product.id)}">
          <label class="pb-card__select"><input type="checkbox" data-bundle-select value="${escapeHTML(product.id)}"${checked ? ' checked' : ''}${this.chosenVariant(product) ? '' : ' disabled'} aria-label="${escapeHTML(this.label('selectProduct'))}: ${escapeHTML(product.title)}"><span class="pb-card__check" aria-hidden="true"></span></label>
          <a class="pb-card__image" href="${escapeHTML(product.url || '#')}" tabindex="-1" aria-hidden="true">${thumb ? `<img src="${escapeHTML(thumb)}" alt="" loading="lazy" width="96" height="96">` : '<span class="pb-card__image-placeholder" aria-hidden="true"></span>'}</a>
          <div class="pb-card__body"><h3 class="pb-card__title"><a href="${escapeHTML(product.url || '#')}" data-bundle-product-link>${escapeHTML(product.title)}</a></h3><p class="pb-card__variant" data-variant-description>${description}</p>
          <button class="pb-card__choose" type="button" data-bundle-choose="${escapeHTML(product.id)}" aria-haspopup="dialog"${product.variants.some((item) => this.usable(item)) ? '' : ' disabled'}>${escapeHTML(this.label('choose'))}</button></div>
          <span class="pb-card__position" aria-hidden="true">${index + 1}</span></article>`;
      }).join('');
      this.updateCardStates();
    }

    updateCardStates() {
      this.products.forEach((product) => {
        const card = this.cards.querySelector(`[data-product-id="${CSS.escape(String(product.id))}"]`);
        if (!card) return;
        const checked = this.selected.has(String(product.id));
        card.classList.toggle('is-selected', checked);
        const checkbox = card.querySelector('[data-bundle-select]');
        if (checkbox) checkbox.checked = checked;
        const variant = this.chosenVariant(product);
        const description = card.querySelector('[data-variant-description]');
        if (description) description.textContent = variant ? `${variant.title && variant.title !== 'Default Title' ? variant.title.replaceAll('&amp;', '&') : this.label('choose')} · ${this.money(variant.price)}` : this.label('choose');
        const productURL = new URL(product.url, location.origin);
        if (variant) productURL.searchParams.set('variant', String(variant.id));
        card.querySelector('[data-bundle-product-link]')?.setAttribute('href', productURL.pathname + productURL.search);
        const image = card.querySelector('.pb-card__image');
        image?.setAttribute('href', productURL.pathname + productURL.search);
        const imageUrl = variant?.image || product.image;
        if (image && imageUrl && image.querySelector('img')?.src !== new URL(imageUrl, location.href).href) image.innerHTML = `<img src="${escapeHTML(imageUrl)}" alt="" loading="lazy" width="96" height="96">`;
      });
    }

    onClick(event) {
      if (this.submitting) return;
      const choose = event.target.closest('[data-bundle-choose]');
      if (choose && this.contains(choose)) { event.preventDefault(); this.openDialog(String(choose.dataset.bundleChoose), choose); return; }
      if (event.target.closest('[data-bundle-close]')) { this.dialog.close(); return; }
      if (event.target.closest('[data-bundle-save]')) { this.saveDialogSelection(); return; }
      if (event.target === this.dialog) {
        const rect = this.dialog.getBoundingClientRect();
        const outside = event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
        if (outside) this.dialog.close();
      }
      if (event.target.closest('[data-bundle-add]')) { event.preventDefault(); this.addToCart(); }
    }

    onChange(event) {
      if (this.submitting) { this.updateCardStates(); this.renderTotals(); return; }
      if (event.target.matches('[data-option-index]')) { this.onDialogChange(event); return; }
      const checkbox = event.target.closest('[data-bundle-select]');
      if (!checkbox || !this.contains(checkbox)) return;
      const id = String(checkbox.value);
      const product = this.products.find((item) => String(item.id) === id);
      if (checkbox.checked) {
        if (product && !this.chosenVariant(product)) {
          checkbox.checked = false;
          this.openDialog(id, checkbox);
          return;
        }
        this.selected.add(id);
      } else this.selected.delete(id);
      this.clearMessage();
      this.updateCardStates();
      this.renderTotals();
    }

    openDialog(productId, trigger) {
      const product = this.products.find((item) => String(item.id) === String(productId));
      if (!product) return;
      this.dialogProduct = product;
      this.dialogFocus = trigger;
      const current = this.chosenVariant(product) || product.variants.find((item) => this.usable(item));
      this.dialogVariant = current;
      if (this.dialogTitle) { this.dialogTitle.textContent = product.title; this.dialogTitle.tabIndex = -1; }
      this.renderDialogOptions();
      if (!this.dialog.open) this.dialog.showModal();
      (this.dialogClose || this.dialogTitle)?.focus();
    }

    renderDialogOptions() {
      const product = this.dialogProduct;
      if (!product) return;
      const selectedValues = product.options.map((option, index) => this.variantOption(this.dialogVariant || {}, option, index));
      const groups = product.options.map((option, index) => {
        const values = Array.isArray(option.values) ? option.values : [];
        const currentValue = selectedValues[index] || '';
        const flavourGroup = option.kind === 'flavour' || /flavou?r/i.test(option.name || '');
        const sizeGroup = option.kind === 'size' || /size/i.test(option.name || '');
        const controls = values.map((value) => {
          const related = product.variants.filter((variant) => this.variantOption(variant, option, index) === String(value));
          const eligible = related.filter((variant) => this.canSelectVariant(variant) && (!sizeGroup || product.options.every((other, otherIndex) => {
            const otherFlavour = other.kind === 'flavour' || /flavou?r/i.test(other.name || '');
            return otherIndex === index || !otherFlavour || this.variantOption(variant, other, otherIndex) === selectedValues[otherIndex];
          })));
          const enabled = eligible.length > 0;
          const checked = currentValue === String(value);
          const sample = eligible[0];
          const photo = flavourGroup && sample?.image ? `<img src="${escapeHTML(sample.image)}" alt="" loading="lazy" width="48" height="48">` : '';
          return `<label class="pb-option${flavourGroup ? ' pb-option--flavour' : ''}${enabled ? '' : ' is-unavailable'}"><input type="radio" name="pb-option-${escapeHTML(product.id)}-${index}" value="${escapeHTML(value)}" data-option-index="${index}"${checked ? ' checked' : ''}${enabled ? '' : ' disabled'}><span>${photo}<span class="pb-option__label">${escapeHTML(String(value).replaceAll('&amp;', '&'))}</span></span></label>`;
        }).join('');
        return `<fieldset class="pb-dialog__group${flavourGroup ? ' pb-dialog__group--flavours' : ''}${sizeGroup ? ' pb-dialog__group--sizes' : ''}"><legend>${escapeHTML(this.formatOptionName(option.name, option.kind))}</legend><div class="pb-dialog__options">${controls}</div></fieldset>`;
      }).join('');
      if (this.dialogContent) this.dialogContent.innerHTML = groups || `<p>${escapeHTML(this.label('none'))}</p>`;
      const image = this.dialogVariant?.image || product.image;
      this.dialogContent.insertAdjacentHTML('afterbegin', `<div class="pb-dialog__product">${image ? `<img src="${escapeHTML(image)}" alt="" width="88" height="88">` : '<span class="pb-dialog__placeholder" aria-hidden="true"></span>'}<span>${escapeHTML((this.dialogVariant?.title || '').replaceAll('&amp;', '&'))}<strong class="pb-dialog__price">${escapeHTML(this.money(this.dialogVariant?.price))}<small>${escapeHTML(this.label('oneTime'))}</small></strong></span></div>`);
      if (this.dialogSave) this.dialogSave.disabled = !this.dialogVariant || !this.usable(this.dialogVariant);
    }

    onDialogChange(event) {
      const input = event.target.closest('[data-option-index]');
      if (!input || !this.dialogProduct) return;
      const product = this.dialogProduct;
      const changedIndex = Number(input.dataset.optionIndex);
      const option = product.options[changedIndex];
      const currentValues = product.options.map((item, index) => this.variantOption(this.dialogVariant || {}, item, index));
      const eligible = product.variants.filter((variant) => this.usable(variant) && this.variantOption(variant, option, changedIndex) === input.value);
      const isFlavour = option.kind === 'flavour' || /flavou?r/i.test(option.name || '');
      const isSize = option.kind === 'size' || /size/i.test(option.name || '');
      const constrained = eligible.filter((variant) => product.options.every((other, otherIndex) => {
        if (otherIndex === changedIndex) return true;
        const otherFlavour = other.kind === 'flavour' || /flavou?r/i.test(other.name || '');
        return !isSize || !otherFlavour || this.variantOption(variant, other, otherIndex) === currentValues[otherIndex];
      }));
      const exact = constrained.find((variant) => product.options.every((other, otherIndex) => otherIndex === changedIndex || this.variantOption(variant, other, otherIndex) === currentValues[otherIndex]));
      const variant = exact || constrained[0] || (isFlavour ? eligible[0] : null);
      if (variant) this.dialogVariant = variant;
      this.renderDialogOptions();
      const next = [...this.dialogContent.querySelectorAll('[data-option-index]')].find((control) => control.dataset.optionIndex === String(changedIndex) && control.value === input.value);
      next?.focus();
    }

    saveDialogSelection() {
      if (!this.dialogProduct || !this.dialogVariant || !this.usable(this.dialogVariant)) return;
      this.dialogProduct.selectedVariantId = String(this.dialogVariant.id);
      this.selected.add(String(this.dialogProduct.id));
      this.clearMessage();
      this.updateCardStates();
      this.renderTotals();
      this.dialog.close();
    }

    onDialogClose() { this.dialogFocus?.focus?.(); this.dialogFocus = null; }

    pricing() {
      const products = this.products.filter((product) => this.selected.has(String(product.id)));
      const variants = products.map((product) => this.chosenVariant(product));
      if (!products.length || variants.some((variant) => !variant)) return {products, variants, regular: 0, current: 0, total: 0, savings: 0, offer: null, ready: false};
      const {current, regular, expected, savings} = calculatePrice(variants, products.length);
      if (products.length === 1) return {products, variants, regular, current, total: current, savings: Math.max(0, regular - current), ready: true, direct: true};
      const offer = this.offers.find((item) => item.key === idKey(variants.map((variant) => variant.id)));
      const ready = !!offer && offer.available === true && Number(offer.price) === expected;
      return {products, variants, regular, current, expected, offer, total: ready ? Number(offer.price) : expected, savings: ready ? Math.max(0, regular - Number(offer.price)) : savings, ready, direct: false};
    }

    renderTotals() {
      const state = this.pricing();
      if (this.total) this.total.textContent = this.money(state.total);
      if (this.regular) { this.regular.textContent = this.money(state.regular); this.regular.hidden = state.regular <= state.total; }
      if (this.savings) { this.savings.textContent = this.money(state.savings); this.savings.parentElement.hidden = state.savings <= 0; }
      const estimateNode = this.querySelector('[data-bundle-estimated]');
      if (estimateNode) {
        estimateNode.hidden = state.products.length < 2 || state.ready;
        estimateNode.textContent = this.label('estimated');
      }
      const usableSelection = state.products.length > 0 && state.variants.every(Boolean);
      this.addButton.disabled = this.submitting || this.cartOutcomeUnknown || !usableSelection || !state.ready;
      this.addButton.setAttribute('aria-disabled', String(this.addButton.disabled));
      if (state.products.length >= 2 && usableSelection && !state.ready) this.setMessage(this.label('notReady'));
      else if (!this.submitting) this.clearMessage();
    }

    setMessage(message, error = false) {
      if (!this.message) return;
      this.message.textContent = message || '';
      this.message.classList.toggle('is-error', !!error);
      this.message.setAttribute('role', error ? 'alert' : 'status');
    }
    clearMessage() { if (this.message) { this.message.textContent = ''; this.message.classList.remove('is-error'); } }

    async fetchWithTimeout(url, options = {}, timeoutMessage = this.label('priceChanged')) {
      const parentSignal = this.abortController?.signal;
      const timeoutMs = 20000;
      let signal;
      let timeoutId;
      let localController;
      let onParentAbort;
      if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function' && typeof AbortSignal.any === 'function') {
        signal = parentSignal ? AbortSignal.any([parentSignal, AbortSignal.timeout(timeoutMs)]) : AbortSignal.timeout(timeoutMs);
      } else {
        localController = new AbortController();
        signal = localController.signal;
        timeoutId = setTimeout(() => localController.abort(), timeoutMs);
        if (parentSignal) {
          onParentAbort = () => localController.abort();
          if (parentSignal.aborted) localController.abort();
          else parentSignal.addEventListener('abort', onParentAbort, {once: true});
        }
      }
      try {
        return await fetch(url, {...options, signal});
      } catch (error) {
        if (parentSignal?.aborted) throw new DOMException('Aborted', 'AbortError');
        if (error.name === 'AbortError' || error.name === 'TimeoutError') throw new Error(timeoutMessage);
        throw error;
      } finally {
        if (timeoutId) clearTimeout(timeoutId);
        if (parentSignal && onParentAbort) parentSignal.removeEventListener('abort', onParentAbort);
      }
    }

    productJSONUrl(product) {
      const url = new URL(product.url || '', location.origin);
      if (url.origin !== location.origin || !url.pathname) throw new Error(this.label('priceChanged'));
      url.search = '';
      url.hash = '';
      url.pathname = `${url.pathname.replace(/\/+$/, '')}.js`;
      return url;
    }

    async loadProductJSON(product) {
      try {
        const response = await this.fetchWithTimeout(this.productJSONUrl(product), {method: 'GET', credentials: 'same-origin', headers: {'X-Requested-With': 'XMLHttpRequest'}});
        if (!response.ok) throw new Error(this.label('priceChanged'));
        return await response.json();
      } catch (error) {
        if (error.name === 'AbortError') throw error;
        throw new Error(this.label('priceChanged'));
      }
    }

    async validateFreshPrices(state) {
      const sourceSnapshots = await Promise.all(state.products.map(async (product, index) => {
        const expected = state.variants[index];
        const freshProduct = await this.loadProductJSON(product);
        const fresh = (freshProduct.variants || []).find((variant) => String(variant.id) === String(expected.id));
        const currentPrice = Number(fresh?.price);
        const compareAtPrice = Number(fresh?.compare_at_price == null ? fresh?.price : fresh.compare_at_price);
        const expectedPrice = Number(expected.price);
        const expectedCompareAt = Number(expected.compareAt == null ? expected.price : expected.compareAt);
        const requiredPlan = freshProduct.requires_selling_plan === true || fresh?.requires_selling_plan === true || fresh?.requiresSellingPlan === true || expected.requires_selling_plan === true || expected.requiresSellingPlan === true;
        if (!fresh || fresh.available !== true || expected.available !== true || requiredPlan || !Number.isSafeInteger(currentPrice) || currentPrice !== expectedPrice || compareAtPrice !== expectedCompareAt) {
          throw new Error(this.label('priceChanged'));
        }
        return {id: String(fresh.id), price: currentPrice, compareAt: compareAtPrice};
      }));
      const freshPrice = calculatePrice(sourceSnapshots, state.products.length).expected;
      if (state.direct) return {sourceSnapshots, expectedPrice: sourceSnapshots[0].price};
      if (freshPrice !== Number(state.expected) || !state.offer?.url) throw new Error(this.label('priceChanged'));
      const offerProduct = await this.loadProductJSON(state.offer);
      const offerVariant = (offerProduct.variants || []).find((variant) => String(variant.id) === String(state.offer.id));
      const offerPrice = Number(offerVariant?.price);
      const offerRequiredPlan = offerVariant?.requires_selling_plan === true || offerVariant?.requiresSellingPlan === true;
      if (!offerVariant || offerVariant.available !== true || state.offer.available !== true || offerRequiredPlan || !Number.isSafeInteger(offerPrice) || offerPrice !== Number(state.offer.price) || offerPrice !== freshPrice) {
        throw new Error(this.label('priceChanged'));
      }
      return {sourceSnapshots, expectedPrice: freshPrice};
    }

    responseConfirmsAdd(result, itemId, state) {
      if (!Array.isArray(result.items) || !result.items.length) return null;
      const lineId = (item) => String(item?.variant_id ?? item?.id ?? '');
      const parent = result.items.find((item) => lineId(item) === String(itemId) && Number(item.quantity) >= 1);
      if (parent) return parent;
      if (state.direct) return null;
      const expectedIds = state.variants.map((variant) => String(variant.id)).sort();
      const exactComponents = (items) => {
        const ids = items.map((item) => lineId(item)).filter(Boolean).sort();
        return ids.length === expectedIds.length && ids.every((id, index) => id === expectedIds[index]) && items.every((item) => Number(item.quantity ?? 1) >= 1);
      };
      const componentParent = result.items.find((item) => Array.isArray(item.item_components) && exactComponents(item.item_components));
      if (componentParent && Number(componentParent.quantity) >= 1) return componentParent;
      return exactComponents(result.items) ? result.items[0] : null;
    }

    async addToCart() {
      if (this.submitting) return;
      if (this.cartOutcomeUnknown) { this.setMessage(this.label('error'), true); return; }
      const state = this.pricing();
      if (!state.ready || !state.products.length || state.variants.some((variant) => !variant)) {
        this.setMessage(state.products.length > 1 ? this.label('notReady') : this.label('choose'), true);
        return;
      }
      const items = state.direct ? [{id: Number(state.variants[0].id), quantity: 1}] : [{id: Number(state.offer.id), quantity: 1}];
      if (!items[0].id || !Number.isSafeInteger(items[0].id)) { this.setMessage(this.label('notReady'), true); return; }
      this.submitting = true;
      const lockedControls = [...this.querySelectorAll('input, [data-bundle-choose]')].map(node => ({node, disabled:node.disabled}));
      lockedControls.forEach(({node}) => { node.disabled = true; });
      this.addButton.disabled = true;
      this.addButton.setAttribute('aria-busy', 'true');
      this.setMessage(this.label('adding'));
      this.abortController = new AbortController();
      let finalMessage = '';
      let finalIsError = false;
      let cartMayHaveSucceeded = false;
      try {
        await this.validateFreshPrices(state);
        const root = window.Shopify?.routes?.root || '/';
        const basePath = root.endsWith('/') ? root : `${root}/`;
        const url = new URL(`${basePath}cart/add.js`, location.origin);
        cartMayHaveSucceeded = true;
        const response = await this.fetchWithTimeout(url, {method: 'POST', credentials: 'same-origin', headers: {'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest'}, body: JSON.stringify({items})}, this.label('error'));
        const result = await response.json();
        const addedLine = this.responseConfirmsAdd(result, items[0].id, state);
        if (!response.ok || result.status >= 400) {
          cartMayHaveSucceeded = false;
          const error = new Error(typeof result.description === 'string' ? result.description : this.label('error'));
          throw error;
        }
        if (!addedLine) throw new Error(this.label('error'));
        cartMayHaveSucceeded = false;
        finalMessage = this.label('added');
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', {detail: {product: result.items?.[0], addToCartBtn: this.addButton}, bubbles: true}));
      } catch (error) {
        if (cartMayHaveSucceeded) {
          this.cartOutcomeUnknown = true;
          finalMessage = this.label('error');
          finalIsError = true;
        } else if (error.name !== 'AbortError') {
          finalMessage = error.message || this.label('error');
          finalIsError = true;
        }
      } finally {
        this.abortController = null;
        this.submitting = false;
        lockedControls.forEach(({node, disabled}) => { node.disabled = disabled; });
        this.addButton.removeAttribute('aria-busy');
        this.renderTotals();
        if (finalMessage) this.setMessage(finalMessage, finalIsError);
      }
    }
  }

  MaxlerProteinBundle.calculatePrice = calculatePrice;
  MaxlerProteinBundle.idKey = idKey;
  customElements.define('maxler-protein-bundle', MaxlerProteinBundle);
})();
